import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth, useLanguage } from '@/contexts';
import { SignUpForm } from './SignUpForm';
import { LoginForm } from './LoginForm';
import SSOSection from './SSOSection';
import { ThemeToggle, LanguageSwitcher } from '@/components/ui';
import { API_URLS } from '../../constants';
import { SignUpFormData, LoginFormData } from '../../types/auth';
import { Tabs } from '../ui';
import { trackEvent } from '@/utils/gtm';
import { GTM_EVENTS } from '@/constants/gtmEvents';

const AuthPage = () => {
  // Get the mode from URL parameters
  const { mode } = useParams<{ mode?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithCredentials } = useAuth();
  const { t } = useLanguage();
  
  // Set initial tab based on URL or default to login
  const [activeTab, setActiveTab] = React.useState(mode === 'signup' ? 'signup' : 'login');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({});
  const [loginSuccess, setLoginSuccess] = React.useState(false);
  const [signUpLoadingMessage, setSignUpLoadingMessage] = React.useState<string | null>(null);
  const [signUpSuccess, setSignUpSuccess] = React.useState(false);
  const [loginLoadingMessage, setLoginLoadingMessage] = React.useState<string | null>(null);
  const [resetPasswordSuccessMessage, setResetPasswordSuccessMessage] = React.useState<string | null>(null);

  // Handle password reset success message from navigation state
  React.useEffect(() => {
    if (location.state?.message) {
      setResetPasswordSuccessMessage(location.state.message);
      // Clear the state to prevent showing message on refresh
      window.history.replaceState({}, document.title);
      
      // Auto-clear message after 5 seconds
      const timer = setTimeout(() => {
        setResetPasswordSuccessMessage(null);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [location.state]);

  const handleLogin = async (data: LoginFormData) => {
    setLoading(true);
    setError(null);
    setFieldErrors({});
    setLoginLoadingMessage(t('signingIn'));
    
    // Track login attempt
    trackEvent({
      event: GTM_EVENTS.LOGIN_ATTEMPT,
      login_method: 'credentials',
    });
    
    try {
  
      const result = await loginWithCredentials(data.email, data.password);
      
      if (result.success) {
  
        // Track login success
        trackEvent({
          event: GTM_EVENTS.LOGIN_SUCCESS,
          login_method: 'credentials',
        });
  
        setLoginLoadingMessage(t("loginSuccessfull"));
        setError(null);
        setFieldErrors({});
        setLoginSuccess(true);
        
        // Redirect after showing success message
        setTimeout(() => {
          setLoginSuccess(false);
          setLoginLoadingMessage(null);
        }, 1500);
      } else {
  
        setLoginLoadingMessage(null);
        
        // Enhanced error handling with specific messages
        const errorMessage = result.error || 'Unknown error';
        const errorType = errorMessage.includes('Invalid email or password') || errorMessage.includes('Invalid credentials')
          ? 'invalid_credentials'
          : errorMessage.includes('Network error') || errorMessage.includes('fetch')
          ? 'network_error'
          : errorMessage.includes('timeout')
          ? 'timeout'
          : 'server_error';
        
        // Track login failure
        trackEvent({
          event: GTM_EVENTS.LOGIN_FAILURE,
          login_method: 'credentials',
          error_type: errorType,
        });
        
        if (errorType === 'invalid_credentials') {
          setFieldErrors({ password: t("incorrectCredentials") });
          setError(null);
        } else if (errorType === 'network_error') {
          setError(t("serverError"));
          setFieldErrors({});
        } else if (errorType === 'timeout') {
          setError(t("timeout"));
          setFieldErrors({});
        } else {
          setError(errorMessage || t("unexpectedError"));
          setFieldErrors({});
        }
      }
    } catch (error) {
  
      setLoginLoadingMessage(null);
      
      // Track login failure
      trackEvent({
        event: GTM_EVENTS.LOGIN_FAILURE,
        login_method: 'credentials',
        error_type: 'exception',
      });
      
      setError(t("networkError"));
      setFieldErrors({});
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (data: SignUpFormData) => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    setFieldErrors({});
    setSignUpLoadingMessage(t("creatingYourAccount"));
    
    // Track signup submit
    trackEvent({
      event: GTM_EVENTS.SIGNUP_SUBMIT,
      function_selected: data.function,
    });
    
    try {
      const response = await fetch(API_URLS.AUTH.REGISTER, {
        method: 'POST',
        credentials: 'include', // Include cookies for automatic login after registration
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          first_name: data.firstName,
          last_name: data.lastName,
          email: data.email,
          password: data.password,
          confirmPassword: data.confirmPassword,
          function: data.function,
        }),
      });

      const result = await response.json();
      
      if (!response.ok) {
        setSignUpLoadingMessage(null);
        
        const errorType = response.status === 409
          ? 'email_exists'
          : response.status === 400
          ? 'validation_error'
          : response.status >= 500
          ? 'server_error'
          : 'unknown_error';
        
        // Track signup failure
        trackEvent({
          event: GTM_EVENTS.SIGNUP_FAILURE,
          error_type: errorType,
        });
        
        if (response.status === 400 && result.errors) {
          // Field-level errors from backend
          const errors: Record<string, string> = {};
          result.errors.forEach((err: { path?: string[]; field?: string; message: string }) => {
            let field = err.path?.[0] || err.field || 'general';
            if (field === 'first_name') field = 'firstName';
            if (field === 'last_name') field = 'lastName';
            if (field === 'function') field = 'function';
            errors[field] = err.message;
          });
          setFieldErrors(errors);
          setError(null);
        } else if (response.status === 409) {
          setFieldErrors({ email: t("emailAlreadyRegistered") });
          setError(null);
        } else if (response.status >= 500) {
          setError(t("serverErrorOccurred"));
          setFieldErrors({});
        } else {
          setError(result.message || t("registrationFailed"));
          setFieldErrors({});
        }
        return;
      }
      
      // Registration successful - track success
      trackEvent({
        event: GTM_EVENTS.SIGNUP_SUCCESS,
        user_function: data.function, // This is not PII
      });

      setSignUpLoadingMessage(t("createdAccount"));
      
      setTimeout(() => {
        setSignUpSuccess(true);
        setError(null);
        setFieldErrors({});
        setSignUpLoadingMessage(t("emailRedirect"));
        
        // Track email verification redirect
        trackEvent({
          event: GTM_EVENTS.EMAIL_VERIFICATION_REDIRECT,
        });
        
        // Redirect to email verification page
        setTimeout(() => {
          setSignUpSuccess(false);
          setSignUpLoadingMessage(null);
          // Mark this as a new user registration for redirect logic
          localStorage.setItem('isNewUserRegistration', 'true');
          navigate(`/auth/verify-email?email=${encodeURIComponent(data.email)}`);
        }, 1500);
      }, 800);
      
    } catch (err: unknown) {
  
      setSignUpLoadingMessage(null);
      
      // Track signup failure
      trackEvent({
        event: GTM_EVENTS.SIGNUP_FAILURE,
        error_type: 'exception',
      });
      
      const errorMessage = err instanceof Error ? err.message : '';
      if (errorMessage.includes('fetch') || errorMessage.includes('Network')) {
        setError(t("serverError"));
      } else {
        setError(t("unexpectedError"));
      }
      setFieldErrors({});
    } finally {
      setLoading(false);
    }
  };

  // Handle tab switching with URL updates
  const switchToLogin = () => {
    setActiveTab('login');
    if (navigate) navigate('/auth/login');
  };
  
  const switchToSignUp = () => {
    setActiveTab('signup');
    if (navigate) navigate('/auth/signup');
  };

  const tabs = [
    {
      id: 'signup',
      label: t('signUp'),
      content: (
        <div className="space-y-4">
          <SignUpForm
            onSubmit={handleSignUp}
            loading={loading}
            onSwitchToLogin={switchToLogin}
            fieldErrors={fieldErrors}
            generalError={error}
            feedbackBelowButton={
              signUpLoadingMessage || signUpSuccess ? (
                <>
                  {signUpLoadingMessage && (
                    <div className="bg-brand-light-gray border border-gray-200 rounded-md text-brand-electric-blue text-sm text-center mt-4">
                      {signUpLoadingMessage}
                    </div>
                  )}
                  {signUpSuccess && (
                    <div className="bg-green-100 border border-green-300 rounded-md text-green-700 text-sm text-center flex items-center justify-center gap-2 mt-4">
                      <svg className="h-5 w-5 text-green-500 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {success}
                    </div>
                  )}
                </>
              ) : null
            }
          />
          <SSOSection disabled={loading} mode="signup" />
        </div>
      ),
    },
    {
      id: 'login',
      label: t('login'),
      content: (
        <div className="space-y-4">
          <LoginForm onSubmit={handleLogin} loading={loading} onSwitchToSignUp={switchToSignUp} fieldErrors={fieldErrors} generalError={error} />
          
          <SSOSection disabled={loading} mode="login" />
          {resetPasswordSuccessMessage && (
            <div className="bg-green-100 text-green-700 border border-green-300 rounded-md p-3 mt-4 text-center text-sm flex items-center justify-center gap-2">
              <svg className="h-5 w-5 text-green-500 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {resetPasswordSuccessMessage}
            </div>
          )}
          {loginSuccess && (
            <div className="bg-green-100 text-green-700 border border-green-300 rounded-md p-3 mt-4 text-center text-sm flex items-center justify-center gap-2">
              <svg className="h-5 w-5 text-green-500 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {t("loginSuccessful")}
            </div>
          )}
          {loginLoadingMessage && (
            <div className="bg-brand-light-gray border border-gray-200 rounded-md text-brand-electric-blue text-sm text-center mt-4">
              {loginLoadingMessage}
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <main className="min-h-screen bg-gray-50 dark:bg-slate-950 transition-colors duration-300 flex flex-col">
      {/* Top Bar with Theme and Language Toggles */}
      <div className="w-full bg-white dark:bg-slate-900 border-b border-gray-100 dark:border-slate-800 transition-colors duration-300">
        <div className="container mx-auto px-4 py-3 flex justify-end gap-2 sm:gap-3">
          <LanguageSwitcher />
          <ThemeToggle />
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center py-4 sm:py-8 px-3 sm:px-4">
        <div className="max-w-md w-full bg-white dark:bg-slate-800 p-4 sm:p-6 rounded-lg shadow-sm dark:shadow-lg dark:shadow-slate-900/50 border border-gray-200 dark:border-slate-700 transition-colors duration-300">
          <div className="text-center mb-4">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-slate-100 transition-colors duration-300">{t('welcome')}</h1>
            <p className="mt-1 text-sm sm:text-base text-gray-600 dark:text-slate-300 transition-colors duration-300">{t('subtitle')}</p>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-slate-400 mt-1 transition-colors duration-300">{t('description')}</p>
          </div>
          
          <Tabs
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={(tab) => {
              setActiveTab(tab);
              if (navigate) navigate(tab === 'login' ? '/auth/login' : '/auth/signup');
            }}
          />
          
          {/* Terms of Service - Only at the bottom */}
          <div className="mt-8 pt-4 border-t border-gray-100 dark:border-slate-700 transition-colors duration-300">
            <p className="text-center text-xs text-gray-500 dark:text-slate-400 transition-colors duration-300">
              {t('signing')}{' '}
              <a href="https://promptshelf.ai/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-brand-electric-blue dark:text-blue-400 hover:text-brand-dark-blue dark:hover:text-blue-300 underline transition-colors duration-300">
                {t('terms')}
              </a>{' '}
              {t('and')}{' '}
              <a href="https://promptshelf.ai/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-brand-electric-blue dark:text-blue-400 hover:text-brand-dark-blue dark:hover:text-blue-300 underline transition-colors duration-300">
                {t('privacyPolicy')}
              </a>
            </p>
          </div>
        </div>
      </div>
    </main>
  );
};

export { AuthPage };
